import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BellRing } from 'lucide-react'
import { useEffect, useId, useRef } from 'react'

import { Switch } from '../../ui/Switch/Switch'
import { PATIENT_REMINDER_PREFERENCE_QUERY_ROOT } from '../patients/patient-reminder-preference-repository'
import type {
  ReminderPolicy,
  ReminderPolicyRepository,
} from './reminder-policy-repository'
import styles from './ReminderPolicyControl.module.css'

function stateLabel(policy: ReminderPolicy) {
  if (policy.effectiveState === 'STOPPING') return 'Stopping'
  if (policy.effectiveState === 'UNAVAILABLE' || !policy.entitled)
    return 'Unavailable'
  return policy.enabled ? 'On' : 'Off'
}

export function ReminderPolicyControl({
  tenantId,
  repository,
  className,
}: {
  tenantId: string
  repository: ReminderPolicyRepository
  className?: string
}) {
  const titleId = useId()
  const client = useQueryClient()
  const queryKey = ['appointment-reminder-policy', tenantId]
  const query = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const policy = await repository.get(tenantId, signal)
      if (policy.tenantUid !== tenantId)
        throw new Error('Policy tenant mismatch')
      return policy
    },
    refetchInterval: (query) =>
      query.state.data?.effectiveState === 'STOPPING' ? 2000 : false,
  })
  const mutation = useMutation({
    mutationFn: async (policy: ReminderPolicy) => {
      if (policy.tenantUid !== tenantId)
        throw new Error('Policy tenant mismatch')
      const result = await repository.update(policy, !policy.enabled)
      if (result.tenantUid !== tenantId)
        throw new Error('Policy tenant mismatch')
      return result
    },
    onSuccess: (policy) => {
      client.setQueryData(queryKey, policy)
      // Each patient's reminder card shows whether the clinic's are on.
      void client.invalidateQueries({
        queryKey: [PATIENT_REMINDER_PREFERENCE_QUERY_ROOT, tenantId],
      })
    },
    onError: () => {
      void client.invalidateQueries({ queryKey })
      void client.invalidateQueries({
        queryKey: [PATIENT_REMINDER_PREFERENCE_QUERY_ROOT, tenantId],
      })
    },
  })
  const policy = query.data
  const switchDisabled =
    !policy ||
    query.isError ||
    query.isFetching ||
    mutation.isPending ||
    policy.effectiveState === 'STOPPING' ||
    (!policy.enabled && !policy.entitled)
  // A disabled switch cannot hold focus: while it saves, focus waits on the
  // card and returns to the switch once it is usable, unless it moved on.
  const cardRef = useRef<HTMLElement>(null)
  const switchTargetRef = useRef<HTMLSpanElement>(null)
  const restoreFocus = useRef(false)
  useEffect(() => {
    if (!restoreFocus.current || switchDisabled) return
    restoreFocus.current = false
    const active = document.activeElement
    if (
      active === null ||
      active === document.body ||
      active === cardRef.current
    )
      switchTargetRef.current?.querySelector('input')?.focus()
    // The status too: a save answered at once may never render as pending.
  }, [switchDisabled, mutation.status])
  const label = policy && !query.isError ? stateLabel(policy) : null
  return (
    <section
      ref={cardRef}
      tabIndex={-1}
      className={[styles.card, className].filter(Boolean).join(' ')}
      aria-labelledby={titleId}
    >
      <span className={styles.sectionLabel}>Automation setting</span>
      <div className={styles.controlRow}>
        <span className={styles.icon} aria-hidden="true">
          <BellRing size={18} strokeWidth={1.75} />
        </span>
        <div className={styles.heading}>
          <h2 id={titleId}>Appointment reminders</h2>
          {label ? (
            <span className={styles.state} data-state={label}>
              {label}
            </span>
          ) : null}
        </div>
        <span ref={switchTargetRef}>
          <Switch
            aria-label="Appointment reminder SMS"
            checked={policy?.enabled ?? false}
            disabled={switchDisabled}
            onChange={() => {
              if (!policy) return
              if (switchTargetRef.current?.contains(document.activeElement)) {
                restoreFocus.current = true
                cardRef.current?.focus()
              }
              mutation.mutate(policy)
            }}
          />
        </span>
      </div>
      <p className={styles.status} role="status">
        {query.isError
          ? 'Reminder settings unavailable.'
          : !policy
            ? 'Loading reminder settings…'
            : mutation.isPending
              ? 'Saving…'
              : policy.effectiveState === 'STOPPING'
                ? 'Turning off. Reminders already in progress are finishing.'
                : policy.effectiveState === 'UNAVAILABLE' || !policy.entitled
                  ? 'Appointment reminders aren’t available for this clinic.'
                  : policy.enabled
                    ? 'Flio can text patients the evening before confirmed appointments, unless their number opted out or staff turned reminders off for them.'
                    : 'No new reminders are sent, and queued ones won’t go out.'}
      </p>
      {mutation.isError && (
        <p className={styles.alert} role="alert">
          The setting couldn’t be saved. It may have changed elsewhere; review
          it and try again.
        </p>
      )}
    </section>
  )
}
